/**
 * Eksportene under «festival-people» i katalogen (docs/EKSPORT.md §9): personer, frivillige,
 * avdelinger, vaktplan, personlig vaktliste og akkreditering.
 * API-halvdelen: `apps/api/src/export/definitions/festival-people/`.
 *
 * Alle er årgangens (`params: editionId`), bortsett fra den personlige vaktlista, som også startes
 * fra «Mine vakter» på tvers av årganger. Ingen er for gjester: Personer-sidene er organisasjonens.
 */
import type { ExportColumnMeta, ExportMeta } from '../types.js';

const col = (
  prefix: string,
  key: string,
  type: ExportColumnMeta['type'],
  defaultHidden = false,
): ExportColumnMeta => ({
  key,
  labelKey: `export.festivalPeople.${prefix}.columns.${key}`,
  type,
  ...(defaultHidden ? { defaultHidden: true } : {}),
});

/**
 * PERSONER: fanen «Personer», med samme filter (avdeling i treet, rolle, akkreditering, søk) og de
 * merkede radene. Kolonnenøklene er tabellens (`tableKey: 'edition-participants'`). Kontaktinfo og
 * «Mat og allergier» er med som standard (EKSPORT.md §5), også når de er skjult på skjermen —
 * maten bare for den som har «Se helseopplysninger».
 */
const PEOPLE: ExportMeta = {
  id: 'festival.people',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalPeople.people.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['participation:list'],
  guest: false,
  tableKey: 'edition-participants',
  columns: [
    col('people', 'name', 'text'),
    /* B1: kategorien, som kolonnen på Personer (`category`). */
    col('people', 'category', 'text'),
    col('people', 'roles', 'text'),
    col('people', 'departments', 'text'),
    col('people', 'email', 'email'),
    col('people', 'phone', 'phone'),
    /* Fra kortet om personen. Med for alle i organisasjonen (PERSONTYPER-BESTILLING §1, 1077). */
    col('people', 'food', 'multiline'),
    col('people', 'sources', 'text', true),
    col('people', 'level', 'text', true),
    col('people', 'days', 'text', true),
    col('people', 'wanted', 'text', true),
    col('people', 'fields', 'multiline', true),
  ],
  params: ['editionId'],
  landscape: true,
};

/**
 * FRIVILLIGE OG PÅMELDTE: fanen «Frivillige», med status, skjema og svarene. Filteret er fanens
 * (status, kilde, skjema, søk, sortering). Svarene er én kolonne med «Spørsmål: svar» per linje,
 * fordi spørsmålene er skjemaets og ikke katalogens.
 */
const VOLUNTEERS: ExportMeta = {
  id: 'festival.volunteers',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalPeople.volunteers.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['volunteer:list'],
  guest: false,
  tableKey: 'edition-volunteers',
  columns: [
    col('volunteers', 'name', 'text'),
    col('volunteers', 'contact', 'email'),
    col('volunteers', 'phone', 'phone'),
    col('volunteers', 'status', 'status'),
    col('volunteers', 'source', 'text'),
    col('volunteers', 'submitted', 'date'),
    col('volunteers', 'shifts', 'integer'),
    col('volunteers', 'answers', 'multiline'),
    col('volunteers', 'level', 'text', true),
    col('volunteers', 'decided', 'date', true),
    col('volunteers', 'consent', 'date', true),
  ],
  params: ['editionId'],
  landscape: true,
};

/**
 * AVDELINGER MED MEDLEMMER: ett kapittel per avdeling (og seksjon), med lederne, medlemmene og
 * postene. Fra avdelingstreet (`object`), merkede (`selection`) og hele årgangen (`parent`).
 */
const DEPARTMENTS: ExportMeta = {
  id: 'festival.departments',
  domain: 'festival',
  kind: 'document',
  titleKey: 'export.festivalPeople.departments.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'selection', 'parent'],
  requires: ['department:list', 'participation:list'],
  guest: false,
  sections: [
    { key: 'leads', labelKey: 'export.festivalPeople.departments.sections.leads' },
    { key: 'members', labelKey: 'export.festivalPeople.departments.sections.members' },
    {
      key: 'posts',
      labelKey: 'export.festivalPeople.departments.sections.posts',
      requires: ['shift:list'],
    },
  ],
  params: ['editionId'],
};

/**
 * VAKTPLANEN: én rad per vakt (per person når den grupperes på person), gruppert etter
 * `params.groupBy` — `department` (standard), `day`, `post` eller `person`. Hele planen
 * (`parent`), uka og avdelingen på skjermen (`filter`), eller de merkede vaktene (`selection`).
 */
const SHIFT_PLAN: ExportMeta = {
  id: 'festival.shift-plan',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalPeople.shiftPlan.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['parent', 'filter', 'selection'],
  requires: ['shift:list'],
  /* H3: en gjest med delt vakt eller avdeling får vaktene delingen når (RLS og `shift:list`). */
  guest: true,
  columns: [
    col('shiftPlan', 'day', 'date'),
    col('shiftPlan', 'start', 'time'),
    col('shiftPlan', 'end', 'time'),
    col('shiftPlan', 'department', 'text'),
    col('shiftPlan', 'post', 'text'),
    col('shiftPlan', 'location', 'text'),
    col('shiftPlan', 'people', 'multiline'),
    col('shiftPlan', 'staffed', 'text'),
    col('shiftPlan', 'missing', 'integer', true),
    col('shiftPlan', 'person', 'text', true),
    col('shiftPlan', 'status', 'status', true),
    col('shiftPlan', 'phone', 'phone', true),
    col('shiftPlan', 'handOuts', 'text', true),
    col('shiftPlan', 'priority', 'status', true),
  ],
  params: ['editionId'],
  landscape: true,
};

/**
 * PERSONLIG VAKTLISTE: ett ark per person med vaktene sine. Fra «Mine vakter» for den innloggede
 * (`object` med egen person, på tvers av årganger når `editionId` mangler), og fra Personer og
 * vaktplanen for merkede personer eller alle med vakter i årgangen.
 */
const SHIFT_LIST: ExportMeta = {
  id: 'festival.shift-list',
  domain: 'festival',
  kind: 'document',
  titleKey: 'export.festivalPeople.shiftList.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'selection', 'parent'],
  requires: ['shift:list'],
  guest: false,
  sections: [
    { key: 'contact', labelKey: 'export.festivalPeople.shiftList.sections.contact' },
    { key: 'shifts', labelKey: 'export.festivalPeople.shiftList.sections.shifts' },
  ],
};

/**
 * AKKREDITERING: lista på fanen, gruppert per nivå eller per passtype (`params.groupBy`: `level`
 * eller `pass`), med antall kort summert.
 */
const ACCREDITATIONS: ExportMeta = {
  id: 'festival.accreditations',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalPeople.accreditations.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter', 'selection', 'parent'],
  requires: ['accreditation:list'],
  guest: false,
  tableKey: 'edition-accreditations',
  columns: [
    col('accreditations', 'name', 'text'),
    col('accreditations', 'source', 'text'),
    col('accreditations', 'context', 'text'),
    col('accreditations', 'level', 'text'),
    col('accreditations', 'pass', 'text'),
    col('accreditations', 'seats', 'integer'),
    col('accreditations', 'handedOut', 'text'),
    col('accreditations', 'email', 'email'),
    col('accreditations', 'zones', 'text', true),
    col('accreditations', 'passDays', 'text', true),
    col('accreditations', 'handedOutAt', 'datetime', true),
    col('accreditations', 'handedOutBy', 'text', true),
    col('accreditations', 'note', 'multiline', true),
  ],
  params: ['editionId'],
  landscape: true,
};

/**
 * UTLEVERINGSLISTE: lista disken krysser av på papir — sortert på navn, med en tom rute for
 * «Utlevert» og et felt for signatur. Ved siden av kortutskriften som finnes, ikke i stedet for den.
 */
const HANDOUT: ExportMeta = {
  id: 'festival.accreditation-handout',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalPeople.handout.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter', 'selection', 'parent'],
  requires: ['accreditation:list'],
  guest: false,
  columns: [
    col('handout', 'name', 'text'),
    col('handout', 'source', 'text'),
    col('handout', 'context', 'text'),
    col('handout', 'level', 'text'),
    col('handout', 'pass', 'text'),
    col('handout', 'seats', 'integer'),
    col('handout', 'handedOut', 'text'),
    col('handout', 'signature', 'text'),
    col('handout', 'email', 'email', true),
    col('handout', 'note', 'multiline', true),
  ],
  params: ['editionId'],
  landscape: true,
};

/**
 * AKKREDITERINGSKORTENE (QA13 punkt 58, sak 253.81): ett kort per side i bankkortformat, 54 × 86 mm
 * stående, i nivåets farge og med organisasjonens logo. Startes fra «Kort til utskrift» med de
 * merkede radene, og valgene der går med som `params.design` og `params.show`
 * (`ACCREDITATION_CARD_DESIGNS`, `ACCREDITATION_CARD_FIELDS`). Bare PDF: et kort er et trykk, ikke en tabell.
 */
const CARDS: ExportMeta = {
  id: 'festival.accreditation-cards',
  domain: 'festival',
  kind: 'document',
  titleKey: 'export.festivalPeople.cards.title',
  formats: ['pdf'],
  scopes: ['selection', 'parent'],
  requires: ['accreditation:list'],
  guest: false,
  params: ['editionId'],
};

export const FESTIVAL_PEOPLE_EXPORTS: readonly ExportMeta[] = [
  PEOPLE,
  VOLUNTEERS,
  DEPARTMENTS,
  SHIFT_PLAN,
  SHIFT_LIST,
  ACCREDITATIONS,
  HANDOUT,
  CARDS,
];
